import { ViewConfig } from '@helo/cms'
import { useLayoutEffect } from 'react'

import { useAppStore } from '../store/appStore/appStore'
import { deriveDetailEntityDisplayString } from '../store/appStore/browserDocumentTitle/deriveDetailEntityDisplayString'

/**
 * Pushes the active CMS view into `browserDocumentTitle`: `viewLabel` from the config,
 * optional entity text from `browserTabTitleEntityContentNode`, and optional drawer suffix
 * via other widgets. For list-style views (no `browserTabTitleEntityContentNode`), when
 * the persisted saved view matches this view (`savedView.viewId === viewConfig.id`),
 * the saved view name is written as `staticTitle` instead of using the composed pattern.
 */
export const ViewDocumentTitle = ({
  viewConfig,
}: {
  viewConfig: ViewConfig
}) => {
  const savedView = useAppStore(s => s.viewStore.savedView)
  const isDetailEntityTitleView = Boolean(
    viewConfig.state.browserTabTitleEntityContentNode,
  )
  const savedViewTabTitle =
    !isDetailEntityTitleView &&
    savedView?.name?.trim() &&
    savedView.viewId === viewConfig.id
      ? savedView.name.trim()
      : null
  const entityRef = viewConfig.state.browserTabTitleEntityContentNode
  const tabQueryId = entityRef?.queryId
  const tabQueryRow = useAppStore(s =>
    tabQueryId ? s.queryStore.queries[tabQueryId] : undefined,
  )

  useLayoutEffect(() => {
    const b = useAppStore.getState().browserDocumentTitle

    if (savedViewTabTitle) {
      b.updateTitle({
        staticTitle: savedViewTabTitle,
        viewLabel: null,
        detailEntityDisplay: null,
        detailDrawerSuffix: null,
      })
      return undefined
    }

    b.updateTitle({
      staticTitle: null,
      viewLabel: viewConfig.state.label.trim() || null,
      detailEntityDisplay: null,
      detailDrawerSuffix: null,
    })

    return undefined
  }, [viewConfig.id, viewConfig.state.label, savedViewTabTitle])

  useLayoutEffect(() => {
    const b = useAppStore.getState().browserDocumentTitle

    if (savedViewTabTitle) {
      b.updateTitle({ detailEntityDisplay: null })
      return
    }

    const ref = viewConfig.state.browserTabTitleEntityContentNode
    if (!ref) {
      return
    }

    const display = deriveDetailEntityDisplayString(
      ref.queryId,
      ref.selector,
      id => useAppStore.getState().queryStore.getQuery(id) ?? null,
    )
    b.updateTitle({ detailEntityDisplay: display })

    return () => {
      useAppStore.getState().browserDocumentTitle.updateTitle({
        detailEntityDisplay: null,
      })
    }
  }, [
    savedViewTabTitle,
    viewConfig.id,
    entityRef?.queryId,
    entityRef?.selector,
    tabQueryRow,
  ])

  return null
}
